import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { MultiSelectComboboxField } from '@/components/form-components/MultiSelectCombobox'
import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { IconProps } from '@/components/icons/types'
import { Button } from '@/components/shadcn/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Collapsible, CollapsibleContent } from '@/components/shadcn/collapsible'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Switch } from '@/components/shadcn/switch'
import { Textarea } from '@/components/shadcn/textarea'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { WarningBanner } from '@/components/ui/WarningBanner'
import { AgentIcon, ReasoningIcon, SkillsIcon, ToolsIcon } from '@/const'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { IconV2CommentOutline } from '@icons/IconV2CommentOutline'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconV2GroupOutline } from '@icons/IconV2GroupOutline'
import { IconV2VaultOutline } from '@icons/IconV2VaultOutline'
import { FC, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

type UserAgentSummary = RestSchemas['Messages.UserAgent.UserAgentSummary']
type NewUserAgent = RestSchemas['Messages.UserAgent.NewUserAgent']
type UpdateUserAgent = RestSchemas['Messages.UserAgent.UpdateUserAgent']

function getAgentErrorMessage(error: unknown, fallback: string): string {
  const visited = new Set<object>()

  const visit = (value: unknown): string | undefined => {
    if (value instanceof Error) {
      const errorWithCause = value as Error & { cause?: unknown }
      return visit(errorWithCause.message) ?? visit(errorWithCause.cause)
    }

    if (typeof value === 'string') {
      const message = value.trim()
      if (!message) {
        return undefined
      }

      try {
        return visit(JSON.parse(message)) ?? message
      } catch {
        return message
      }
    }

    if (Array.isArray(value)) {
      const messages = value.map(visit).filter((message): message is string => !!message)
      return messages.length > 0 ? messages.join('\n') : undefined
    }

    if (!value || typeof value !== 'object') {
      return undefined
    }

    if (visited.has(value)) {
      return undefined
    }
    visited.add(value)

    const record = value as Record<string, unknown>
    for (const key of ['message', 'Message', 'detail', 'error', 'errors', 'title', 'cause']) {
      const message = visit(record[key])
      if (message) {
        return message
      }
    }

    const nestedMessages = Object.values(record)
      .map(visit)
      .filter((message): message is string => !!message)
    return nestedMessages.length > 0 ? nestedMessages.join('\n') : undefined
  }

  return visit(error) ?? fallback
}

const subAgentSchema = z.object({
  key: z.string().min(1, 'Sub-agent key is required').max(60),
  instructions: z.string().min(1, 'Instructions are required'),
  description: z.string().max(256).default(''),
  toolsets: z.array(z.string()).default([]),
  model: z.string().default(''),
})

const formSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(60, 'Name cannot exceed 60 characters'),
    key: z.string().min(1, 'Key is required').max(60, 'Key cannot exceed 60 characters'),
    description: z.string().max(256, 'Description cannot exceed 256 characters').default(''),
    providerKey: z.string().min(1, 'Provider key is required').max(50, 'Provider key cannot exceed 50 characters'),
    model: z.string().min(1, 'Model is required').max(120, 'Model cannot exceed 120 characters'),
    systemPrompt: z.string().max(65536, 'System prompt cannot exceed 65536 characters'),
    temperature: z.coerce.number().min(0).max(2).default(0),
    maxTokens: z.coerce.number().int().min(1).default(1500),
    tools: z.array(z.string()).default([]),
    locked: z.boolean().default(false),
    contextManagementEnabled: z.boolean().default(false),
    contextWindow: z.coerce.number().int().min(0).default(0),
    compactorTokenThresholdPct: z.coerce.number().min(0.05).max(0.95).default(0.7),
    compactorMessageThreshold: z.coerce.number().int().min(1).default(40),
    compactorKeepRecent: z.coerce.number().int().min(1).default(20),
    compactorSummaryMaxTokens: z.coerce.number().int().min(1).default(800),
    compactorModel: z.string().max(120, 'Compactor model cannot exceed 120 characters').default(''),
    contextStrategy: z.enum(['none', 'sliding', 'summarize', 'arq_compactor']).default('none'),
    multiAgentEnabled: z.boolean().default(false),
    parallelSubAgents: z.boolean().default(false),
    orchestratorModel: z.string().default(''),
    toolsetKeys: z.array(z.string()).default([]),
    skillKeys: z.array(z.string()).default([]),
    subAgents: z.array(subAgentSchema).default([]),
    codeExecutionEnabled: z.boolean().default(false),
    isPublic: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.contextManagementEnabled && data.contextStrategy === 'none') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose a context strategy when context management is enabled',
        path: ['contextStrategy'],
      })
    }

    if (data.contextManagementEnabled && data.compactorKeepRecent >= data.compactorMessageThreshold) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Must be less than the message threshold',
        path: ['compactorKeepRecent'],
      })
    }
  })

export type TDmpChatAgentForm = z.infer<typeof formSchema>

type DmpChatAgentFormProps = {
  onSubmit?: (agent: UserAgentSummary) => void
  onDelete?: () => void
  sheetTrigger?: ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedAgent?: UserAgentSummary
  open?: boolean
  onOpenChange?: (open: boolean) => void
  layout?: 'stacked' | 'grid'
}

export function DmpChatAgentForm(props: DmpChatAgentFormProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const isControlled = props.open !== undefined
  const isOpen = props.open ?? sheetOpen
  const edit = !!props.selectedAgent
  const { onOpenChange } = props

  const {
    getUserAgentApiGetUserAgent,
    getUserAgentApiGetUserAgents,
    postUserAgentApiUpdateUserAgent,
    putUserAgentApiAddUserAgent,
    deleteUserAgentApiDeleteUserAgent,
  } = useRestClient()

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!isControlled) {
        setSheetOpen(open)
      }
      onOpenChange?.(open)
    },
    [isControlled, onOpenChange]
  )

  const { mutate: updateAgent, isPending: isUpdating } = postUserAgentApiUpdateUserAgent.useMutation(undefined, {
    onSuccess: (saved) => {
      getUserAgentApiGetUserAgents.invalidateQueries()
      getUserAgentApiGetUserAgent.invalidateQueries()
      handleOpenChange(false)
      toast.success('Agent successfully updated')
      props.onSubmit?.(saved)
    },
    onError: (error) => {
      toast.error(getAgentErrorMessage(error, 'Error updating agent'))
    },
  })

  const { mutate: addAgent, isPending: isAdding } = putUserAgentApiAddUserAgent.useMutation(undefined, {
    onSuccess: (saved) => {
      getUserAgentApiGetUserAgents.invalidateQueries()
      handleOpenChange(false)
      toast.success('Agent successfully added')
      props.onSubmit?.(saved)
    },
    onError: (error) => {
      toast.error(getAgentErrorMessage(error, 'Error adding agent'))
    },
  })

  const agentId = props.selectedAgent?.id
  const { mutate: deleteAgent, isPending: isDeleting } = deleteUserAgentApiDeleteUserAgent.useMutation(undefined, {
    onSuccess: () => {
      getUserAgentApiGetUserAgents.invalidateQueries()
      getUserAgentApiGetUserAgent.invalidateQueries()
      handleOpenChange(false)
      toast.success('Agent successfully deleted')
      props.onDelete?.()
    },
    onError: () => {
      toast.error('Error deleting agent')
    },
  })

  const handleDelete = useCallback(() => {
    if (agentId === undefined || agentId === null) {
      return
    }

    deleteAgent({ path: { agentId } })
  }, [agentId, deleteAgent])

  const submitHandler = useCallback(
    (data: TDmpChatAgentForm) => {
      const config = {
        key: data.key,
        name: data.name,
        description: data.description || null,
        providerKey: data.providerKey,
        model: data.model,
        temperature: data.temperature,
        maxTokens: data.maxTokens,
        tools: data.tools,
        systemPrompt: data.systemPrompt,
        locked: data.locked,
        contextWindow: data.contextWindow,
        contextManagementEnabled: data.contextManagementEnabled,
        compactorTokenThresholdPct: data.compactorTokenThresholdPct,
        compactorMessageThreshold: data.compactorMessageThreshold,
        compactorKeepRecent: data.compactorKeepRecent,
        compactorSummaryMaxTokens: data.compactorSummaryMaxTokens,
        compactorModel: data.compactorModel,
        contextStrategy: data.contextStrategy,
        multiAgentEnabled: data.multiAgentEnabled,
        parallelSubAgents: data.parallelSubAgents,
        orchestratorModel: data.orchestratorModel,
        toolsetKeys: data.toolsetKeys,
        skillKeys: data.skillKeys,
        subAgents: data.subAgents,
        builtinFeatures: {
          ...(props.selectedAgent?.config?.builtinFeatures ?? {}),
          code_execution: data.codeExecutionEnabled,
        },
      }

      if (edit && props.selectedAgent?.id) {
        const updatedAgent: UpdateUserAgent = {
          id: props.selectedAgent.id,
          name: data.name,
          isPublic: data.isPublic,
          config,
        }
        updateAgent({ body: updatedAgent })
        return
      }

      const newAgent: NewUserAgent = {
        name: data.name,
        isPublic: data.isPublic,
        config,
      }
      addAgent({ body: newAgent })
    },
    [addAgent, edit, props.selectedAgent, updateAgent]
  )

  const isLoading = isUpdating || isAdding
  if (isLoading) {
    return <FormComponentLoading />
  }

  const showSheet = Boolean(props.sheetTrigger || props.openInSheet || isControlled)
  const sheetTitle = props.sheetTitle ?? (edit ? 'Edit agent' : 'New agent')
  const sheetDescription =
    props.sheetDescription ?? (edit ? 'Update your DMP Chat agent configuration.' : 'Create a new DMP Chat agent.')

  return showSheet ? (
    <SheetWithFormComponent
      description={sheetDescription}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      title={sheetTitle}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
    >
      <FormComponent
        layout={props.layout}
        selectedAgent={props.selectedAgent}
        submitHandler={submitHandler}
        onDelete={props.onDelete}
        deleteAgent={handleDelete}
        isDeleting={isDeleting}
      />
    </SheetWithFormComponent>
  ) : (
    <FormComponent
      layout={props.layout}
      selectedAgent={props.selectedAgent}
      submitHandler={submitHandler}
      onDelete={props.onDelete}
      deleteAgent={handleDelete}
      isDeleting={isDeleting}
    />
  )
}

type FormComponentProps = {
  layout?: 'stacked' | 'grid'
  selectedAgent?: UserAgentSummary
  submitHandler: (data: TDmpChatAgentForm) => void
  onDelete?: () => void
  deleteAgent: () => void
  isDeleting: boolean
}

type AgentFormSectionProps = {
  children: ReactNode
  className?: string
  contentClassName?: string
  description: string
  icon: FC<IconProps>
  layout: 'stacked' | 'grid'
  title: string
}

function AgentFormSection({
  children,
  className,
  contentClassName,
  description,
  icon: Icon,
  layout,
  title,
}: AgentFormSectionProps) {
  if (layout === 'grid') {
    return (
      <Card className={cn('h-full pt-0', className)}>
        <CardHeader className="border-b bg-muted py-5">
          <div className="flex items-start gap-5">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
              <Icon aria-hidden="true" className="size-8" />
            </div>
            <div className="min-w-0 space-y-1">
              <CardTitle className="font-semibold">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className={cn('space-y-4', contentClassName)}>{children}</CardContent>
      </Card>
    )
  }

  return (
    <section className={cn('space-y-4 rounded-lg border p-5', className)}>
      <div>
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  )
}

function FormComponent({
  layout = 'stacked',
  selectedAgent,
  submitHandler,
  onDelete,
  deleteAgent,
  isDeleting,
}: FormComponentProps) {
  const edit = !!selectedAgent
  const { getSkillApiGetSkills, getToolRegistrationApiListTools, getToolsetRegistrationApiListToolsets } =
    useRestClient()
  const { data: availableSkills } = getSkillApiGetSkills.useQuery(undefined)
  const { data: availableTools } = getToolRegistrationApiListTools.useQuery(undefined)
  const { data: availableToolsets } = getToolsetRegistrationApiListToolsets.useQuery(undefined)

  const defaultValues = useMemo<TDmpChatAgentForm>(
    () => ({
      name: selectedAgent?.name ?? selectedAgent?.config?.name ?? 'Project Agent',
      key: selectedAgent?.config?.key ?? 'project',
      description: selectedAgent?.config?.description ?? '',
      providerKey: selectedAgent?.config?.providerKey ?? 'openai',
      model: selectedAgent?.config?.model ?? 'azure.gpt-4.1-2025-04-14',
      systemPrompt: selectedAgent?.config?.systemPrompt ?? 'You are the DMP project assistant.',
      temperature: selectedAgent?.config?.temperature ?? 0,
      maxTokens: selectedAgent?.config?.maxTokens ?? 1500,
      tools: selectedAgent?.config?.tools ?? [],
      locked: selectedAgent?.config?.locked ?? false,
      contextManagementEnabled: selectedAgent?.config?.contextManagementEnabled ?? false,
      contextWindow: selectedAgent?.config?.contextWindow ?? 0,
      compactorTokenThresholdPct: selectedAgent?.config?.compactorTokenThresholdPct ?? 0.7,
      compactorMessageThreshold: selectedAgent?.config?.compactorMessageThreshold ?? 40,
      compactorKeepRecent: selectedAgent?.config?.compactorKeepRecent ?? 20,
      compactorSummaryMaxTokens: selectedAgent?.config?.compactorSummaryMaxTokens ?? 800,
      compactorModel: selectedAgent?.config?.compactorModel ?? '',
      contextStrategy: (selectedAgent?.config?.contextStrategy as TDmpChatAgentForm['contextStrategy']) ?? 'none',
      multiAgentEnabled: selectedAgent?.config?.multiAgentEnabled ?? false,
      parallelSubAgents: selectedAgent?.config?.parallelSubAgents ?? false,
      orchestratorModel: selectedAgent?.config?.orchestratorModel ?? '',
      toolsetKeys: selectedAgent?.config?.toolsetKeys ?? [],
      skillKeys: selectedAgent?.config?.skillKeys ?? [],
      subAgents:
        selectedAgent?.config?.subAgents?.map((subAgent) => ({
          key: subAgent.key ?? '',
          instructions: subAgent.instructions ?? '',
          description: subAgent.description ?? '',
          toolsets: subAgent.toolsets ?? [],
          model: subAgent.model ?? '',
        })) ?? [],
      codeExecutionEnabled: selectedAgent?.config?.builtinFeatures?.code_execution ?? false,
      isPublic: selectedAgent?.isPublic ?? false,
    }),
    [selectedAgent]
  )

  const form = useForm<TDmpChatAgentForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const {
    fields: subAgentFields,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: 'subAgents',
  })

  const contextManagementEnabled = form.watch('contextManagementEnabled')
  const multiAgentEnabled = form.watch('multiAgentEnabled')
  const toolOptions = useMemo(
    () =>
      (availableTools ?? [])
        .filter((tool) => tool.functionName)
        .map((tool) => ({
          value: tool.functionName!,
          label: tool.name,
          description: tool.description ?? tool.functionName ?? undefined,
          group: tool.moduleName ?? 'Uncategorized',
        })),
    [availableTools]
  )
  const toolsetOptions = useMemo(
    () =>
      (availableToolsets ?? [])
        .filter((toolset) => toolset.applicationId == null)
        .map((toolset) => ({
          value: toolset.key,
          label: toolset.name,
          description: toolset.description ?? undefined,
        })),
    [availableToolsets]
  )
  const skillOptions = useMemo(
    () =>
      [...(availableSkills ?? [])]
        .filter((skill) => !skill.isPlatform)
        .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')))
        .flatMap((skill) => {
          const key = skill.key
          if (!key) {
            return []
          }
          const label = skill.name ?? key
          return [
            {
              value: key,
              label,
              description: skill.description ? `${key} — ${skill.description}` : key,
            },
          ]
        }),
    [availableSkills]
  )

  const handleInvalidSubmit = useCallback(() => {
    toast.error('Please fix validation errors before submitting')
  }, [])
  const handleSubmit = form.handleSubmit(submitHandler, handleInvalidSubmit)

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit}>
        <div className="flex h-full flex-col">
          <div className={cn(layout === 'grid' ? 'grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3' : 'space-y-6')}>
            <AgentFormSection
              className={layout === 'grid' ? 'lg:col-span-2' : undefined}
              contentClassName={layout === 'grid' ? 'flex flex-1 flex-col' : undefined}
              description="Persistent instructions that establish the agent’s role and behavior."
              icon={IconV2CommentOutline}
              layout={layout}
              title="System prompt"
            >
              <FormField
                control={form.control}
                name="systemPrompt"
                render={({ field }) => (
                  <FormItem className="flex flex-1 flex-col">
                    <FormLabel>System prompt</FormLabel>
                    <FormControl>
                      <Textarea
                        className="min-h-[18rem] flex-1 resize-y"
                        {...field}
                        rows={layout === 'grid' ? 12 : 6}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AgentFormSection>

            <AgentFormSection
              description="Define the agent identity and the model used for its normal responses."
              icon={AgentIcon}
              layout={layout}
              title="Identity and model"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormDescription>
                      Display name for the agent. This is also stored as the configuration name.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Key *</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={edit} />
                    </FormControl>
                    <FormDescription>
                      Stable identifier used by DMP Chat to find this agent. It cannot be changed after creation.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormDescription>Short explanation shown wherever the agent is presented to users.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="providerKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Provider key *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormDescription>
                        Registered provider key used to resolve credentials and adapter settings.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="model"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Model *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormDescription>Model identifier sent to the selected provider.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="temperature"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Temperature</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} max={2} step={0.1} {...field} />
                      </FormControl>
                      <FormDescription>
                        Controls response randomness. Higher values produce more varied output.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="maxTokens"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max tokens</FormLabel>
                      <FormControl>
                        <Input type="number" min={1} step={1} {...field} />
                      </FormControl>
                      <FormDescription>
                        Maximum number of tokens the model may generate in one response.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AgentFormSection>

            <AgentFormSection
              description="Select individual registered tools or whole platform toolsets available to the orchestrator."
              icon={ToolsIcon}
              layout={layout}
              title="Tool access"
            >
              <MultiSelectComboboxField
                control={form.control}
                name="tools"
                label="Tools"
                options={toolOptions}
                placeholder="Select individual tools..."
                searchPlaceholder="Search tools..."
                emptyMessage="No registered tools found."
              />
              <p className="text-sm text-muted-foreground">
                Individual function names are the legacy tool format. Prefer toolsets when you want a complete bundle.
              </p>
              <MultiSelectComboboxField
                control={form.control}
                name="toolsetKeys"
                label="Toolsets"
                options={toolsetOptions}
                placeholder="Select platform toolsets..."
                searchPlaceholder="Search toolsets..."
                emptyMessage="No platform toolsets found."
              />
              <p className="text-sm text-muted-foreground">
                Toolsets are validated against platform registrations and attach all tools in the selected bundles.
              </p>
            </AgentFormSection>

            <AgentFormSection
              description="Select reusable skills from the agent skill registry. Platform skills are managed automatically."
              icon={SkillsIcon}
              layout={layout}
              title="Skills"
            >
              <MultiSelectComboboxField
                control={form.control}
                name="skillKeys"
                label="Agent skills"
                options={skillOptions}
                placeholder="Select agent skills..."
                searchPlaceholder="Search agent skills..."
                emptyMessage="No user skills found."
              />
            </AgentFormSection>

            <AgentFormSection
              description="Control how long conversations are kept within the model context window."
              icon={ReasoningIcon}
              layout={layout}
              title="Context management"
            >
              <FormField
                control={form.control}
                name="contextManagementEnabled"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <div>
                      <FormLabel>Enable context management</FormLabel>
                      <FormDescription>
                        Allow DMP Chat to compact or trim older messages instead of sending the full history every turn.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Collapsible open={contextManagementEnabled}>
                <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                  <div className="space-y-4 rounded-md border-l-[10px] border-l-tangarine-300 bg-tangarine-50 p-5">
                    <SingleSelectComboboxField
                      control={form.control}
                      name="contextStrategy"
                      label="Context strategy"
                      options={[
                        {
                          value: 'arq_compactor',
                          label: 'ARQ compactor',
                          description: 'Preserve references such as query_ref and catalog components.',
                        },
                        {
                          value: 'summarize',
                          label: 'Summarize',
                          description: 'Use an LLM to summarize older conversation turns.',
                        },
                        {
                          value: 'sliding',
                          label: 'Sliding window',
                          description: 'Drop the oldest turns without an LLM summarization call.',
                        },
                        { value: 'none', label: 'None', description: 'Do not apply a context strategy.' },
                      ]}
                      description="A non-none strategy is required for context management to have an effect."
                      placeholder="Select a context strategy..."
                      searchPlaceholder="Search strategies..."
                    />
                    <div className="grid gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="contextWindow"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Context window</FormLabel>
                            <FormControl>
                              <Input type="number" min={0} step={1} {...field} />
                            </FormControl>
                            <FormDescription>
                              Override the model context size in tokens. Use 0 for the built-in model lookup.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="compactorModel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Compactor model</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription>
                              Optional model used for summaries. Leave empty to reuse the main model.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="compactorTokenThresholdPct"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Token threshold</FormLabel>
                            <FormControl>
                              <Input type="number" min={0.05} max={0.95} step={0.05} {...field} />
                            </FormControl>
                            <FormDescription>
                              Compact when this fraction of the context window is reached, from 0.05 to 0.95.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="compactorMessageThreshold"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message threshold</FormLabel>
                            <FormControl>
                              <Input type="number" min={2} step={1} {...field} />
                            </FormControl>
                            <FormDescription>
                              Compact after this many unsummarized messages, even if token usage is low.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="compactorKeepRecent"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Keep recent messages</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} step={1} {...field} />
                            </FormControl>
                            <FormDescription>
                              Keep this many recent messages verbatim in the compacted context.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="compactorSummaryMaxTokens"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Summary max tokens</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} step={1} {...field} />
                            </FormControl>
                            <FormDescription>Maximum number of tokens in the generated summary.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </AgentFormSection>

            <AgentFormSection
              description="Configure an orchestrator and optional specialist agents for delegated work."
              icon={IconV2GroupOutline}
              layout={layout}
              title="Multi-agent team"
            >
              <FormField
                control={form.control}
                name="multiAgentEnabled"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <div>
                      <FormLabel>Enable multi-agent mode</FormLabel>
                      <FormDescription>
                        Use the main agent as an orchestrator that can delegate work to specialists.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Collapsible open={multiAgentEnabled}>
                <CollapsibleContent className="space-y-4 overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                  <div className="space-y-4 rounded-md border-l-[10px] border-l-tangarine-300 bg-tangarine-50 p-5">
                    <FormField
                      control={form.control}
                      name="parallelSubAgents"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between gap-3 rounded-md border p-3">
                          <div>
                            <FormLabel>Run specialists in parallel</FormLabel>
                            <FormDescription>Allow independent delegated tasks to run concurrently.</FormDescription>
                          </div>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="orchestratorModel"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Orchestrator model</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormDescription>
                            Optional model for delegation decisions. Leave empty to use the main model.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="space-y-4 rounded-md bg-muted/30 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h4 className="font-medium">Specialist agents</h4>
                          <p className="text-sm text-muted-foreground">
                            Define the agents the orchestrator may delegate to.
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() =>
                            append({ key: '', instructions: '', description: '', toolsets: [], model: '' })
                          }
                        >
                          Add specialist
                        </Button>
                      </div>
                      {subAgentFields.length === 0 && (
                        <p className="text-sm text-muted-foreground">No specialists configured.</p>
                      )}
                      {subAgentFields.map((field, index) => (
                        <div key={field.id} className="space-y-4 rounded-md border bg-background p-4">
                          <div className="flex items-center justify-between gap-3">
                            <h5 className="font-medium">Specialist {index + 1}</h5>
                            <Button type="button" variant="ghost" onClick={() => remove(index)}>
                              Remove
                            </Button>
                          </div>
                          <div className="grid gap-4 md:grid-cols-2">
                            <FormField
                              control={form.control}
                              name={`subAgents.${index}.key` as const}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Key *</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormDescription>
                                    Unique identifier used when delegating to this specialist.
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`subAgents.${index}.model` as const}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Model</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormDescription>
                                    Optional model override. Leave empty to use the worker default.
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <FormField
                            control={form.control}
                            name={`subAgents.${index}.description` as const}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormDescription>Short summary of the specialist’s responsibility.</FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`subAgents.${index}.instructions` as const}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Instructions *</FormLabel>
                                <FormControl>
                                  <Textarea {...field} rows={4} />
                                </FormControl>
                                <FormDescription>
                                  Prompt that explains how this specialist should perform delegated work.
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <MultiSelectComboboxField
                            control={form.control}
                            name={`subAgents.${index}.toolsets` as const}
                            label="Toolsets"
                            options={toolsetOptions}
                            placeholder="Select specialist toolsets..."
                            searchPlaceholder="Search toolsets..."
                            emptyMessage="No platform toolsets found."
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
              <FormField
                control={form.control}
                name="codeExecutionEnabled"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-3 rounded-md border border-dashed p-3">
                    <div>
                      <FormLabel>Enable code execution</FormLabel>
                      <FormDescription>
                        Add the built-in python_executor specialist with isolated sandbox access. The backend manages
                        that specialist and removes sandbox access from the orchestrator.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </AgentFormSection>

            <AgentFormSection
              description="Control who can use the agent and whether its configuration is protected."
              icon={IconV2VaultOutline}
              layout={layout}
              title="Availability and protection"
            >
              <FormField
                control={form.control}
                name="isPublic"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <div>
                      <FormLabel>Public agent</FormLabel>
                      <FormDescription>
                        Allow all users to run this agent instead of limiting it to the owner and members.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="locked"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <div>
                      <FormLabel>Locked configuration</FormLabel>
                      <FormDescription>
                        Mark the agent configuration as locked to prevent normal modifications.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </AgentFormSection>
            {layout === 'grid' && edit && onDelete && (
              <DeleteAgentAction
                agentName={
                  selectedAgent?.name ?? selectedAgent?.config?.name ?? selectedAgent?.config?.key ?? 'this agent'
                }
                isSubmitting={isDeleting}
                onConfirm={deleteAgent}
              />
            )}
          </div>
          {layout !== 'grid' && (
            <div className="form-action-container">
              <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
            </div>
          )}
        </div>
      </form>
      {layout === 'grid' && form.formState.isDirty && (
        <div className="sticky bottom-0 z-20 mt-5 bg-offwhite/95 py-3 backdrop-blur">
          <WarningBanner
            message="You have unsaved agent changes. Update before navigating away to keep your changes."
            actionLabel="Update agent"
            onAction={() => {
              void handleSubmit()
            }}
          />
        </div>
      )}
    </Form>
  )
}

function DeleteAgentAction({
  agentName,
  isSubmitting,
  onConfirm,
}: {
  agentName: string
  isSubmitting: boolean
  onConfirm: () => void
}) {
  return (
    <div className="flex h-full items-center justify-center">
      <ConfirmDialog
        title="Delete agent?"
        description={`Are you sure you want to delete "${agentName}"? This action cannot be undone.`}
        confirmLabel="Delete agent"
        confirmVariant="destructive"
        closeOnConfirm={false}
        isSubmitting={isSubmitting}
        onConfirm={onConfirm}
        trigger={
          <Button type="button" variant="destructive">
            <IconDeleteOutline className="size-4" />
            Delete agent
          </Button>
        }
      />
    </div>
  )
}
